import { DEFAULT_NEWNESS_LOOKBACK_DAYS } from './dateOverlap';

type SetDefinition = Models.SetsPlayground.SetDefinition;
type SetDistribution = Models.SetsPlayground.SetDistribution;

// "newest" is a reserved set name
export const NEWEST_SET_NAME = 'newest';

export const isReservedSetName = (name: string): boolean => name.trim().toLowerCase() === NEWEST_SET_NAME;

// No sets is priority of 0. Otherwise use maxPriority + 1
export const calculateNewnessPriority = (sets: SetDefinition[]): number => (
    sets.length === 0 ? 0 : Math.max(...sets.map((set) => set.priority)) + 1
);

export const DEFAULT_NEWNESS_DISTRIBUTION: SetDistribution = {
    type: 'backpressure',
    part: 0.1,
    offset: 3,
};

export type NewestSetFilters = Models.SetsPlayground.SetFilterRequest & {
    content: NonNullable<Models.SetsPlayground.SetFilterRequest['content']> & {
        lookbackPeriodDays: string[];
    };
};

export type NewestSetDefinition = SetDefinition & {
    filters: NewestSetFilters;
    groupMatchingMode: 'allElements';
};

export const buildNewestSet = (
    lookbackPeriodDays: number,
    priority: number,
    distribution: SetDistribution = DEFAULT_NEWNESS_DISTRIBUTION,
): NewestSetDefinition => ({
    name: NEWEST_SET_NAME,
    priority,
    distribution,
    ranking: {
        type: 'forced',
        rankingStrategies: [
            {
                name: '',
                sortingDirection: 'descending',
            },
        ],
    },
    groupMatchingMode: 'allElements',
    filters: { content: { lookbackPeriodDays: [String(lookbackPeriodDays)] } },
});

export interface ExtractedNewness {
    newnessEnabled: boolean;
    newnessLookbackDays: number | null;
    newnessDistribution: SetDistribution;
    sets: SetDefinition[];
}

export const extractNewnessFromSets = (sets: SetDefinition[]): ExtractedNewness => {
    const index = sets.findIndex((set) => isReservedSetName(set.name));

    if (index === -1) {
        return {
            newnessEnabled: false,
            newnessLookbackDays: DEFAULT_NEWNESS_LOOKBACK_DAYS,
            newnessDistribution: DEFAULT_NEWNESS_DISTRIBUTION,
            sets,
        };
    }

    const newestSet = sets[index] as Partial<NewestSetDefinition>;
    const lookbackPeriodDays = newestSet.filters?.content?.lookbackPeriodDays?.[0];

    return {
        newnessEnabled: true,
        newnessLookbackDays: lookbackPeriodDays !== undefined ? Number(lookbackPeriodDays) : DEFAULT_NEWNESS_LOOKBACK_DAYS,
        newnessDistribution: newestSet.distribution ?? DEFAULT_NEWNESS_DISTRIBUTION,
        sets: [...sets.slice(0, index), ...sets.slice(index + 1)],
    };
};

// Newness set exists as an additional 'custom' set
export const injectNewnessIntoSets = (
    sets: SetDefinition[],
    newnessEnabled: boolean,
    newnessLookbackDays: number | null,
    newnessDistribution?: SetDistribution | null,
): SetDefinition[] => {
    if (!newnessEnabled) {
        return sets;
    }

    const priority = calculateNewnessPriority(sets);
    const lookbackDays = newnessLookbackDays ?? DEFAULT_NEWNESS_LOOKBACK_DAYS;
    const distribution = newnessDistribution ?? DEFAULT_NEWNESS_DISTRIBUTION;

    return [...sets, buildNewestSet(lookbackDays, priority, distribution)];
};
